import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useAnalytics} from '@github-ui/use-analytics'
import {useDebounce} from '@github-ui/use-debounce'
import {startTransition, useCallback, useEffect, useRef, useState} from 'react'

import {LowQualityWarningBanner} from '../components/LowQualityWarningBanner'
import {isLowQualityComment} from '../utils/low-quality-comment'

const LowQualityWarningState = {
  Idle: 'idle',
  Showing: 'showing',
  Dismissed: 'dismissed',
} as const

type LowQualityWarningState = (typeof LowQualityWarningState)[keyof typeof LowQualityWarningState]

/** Minimum length to check for low-quality comments. Shortest realistic low-quality comment is 2 chars (e.g., "+1", "ok"). */
const MIN_LENGTH_FOR_LOW_QUALITY_CHECK = 2

interface UseLowQualityWarningOptions {
  /** The markdown content to check for low-quality patterns */
  markdownBody: string
  /** Optional callback invoked when the warning banner is dismissed */
  onDismiss?: () => void
}

interface UseLowQualityWarningResult {
  /** The banner element to render, or undefined if not showing */
  banner: React.ReactNode | undefined
  /** Whether the warning is currently being displayed */
  isShowing: boolean
}

/**
 * Hook that detects low-quality comments and returns a warning banner.
 *
 * Handles:
 * - Feature flag check (`issues_react_low_quality_comment_warning`)
 * - Debounced detection of low-quality patterns
 * - State management (Idle → Showing → Dismissed)
 * - Analytics events for shown/dismissed
 *
 * @example
 * ```tsx
 * const { banner } = useLowQualityWarning({
 *   markdownBody,
 *   onDismiss: () => commentBoxRef.current?.focus(),
 * })
 *
 * return <CommentBox infoBanner={banner} ... />
 * ```
 */
export function useLowQualityWarning({
  markdownBody,
  onDismiss,
}: UseLowQualityWarningOptions): UseLowQualityWarningResult {
  const {sendAnalyticsEvent} = useAnalytics()

  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags
  const lowQualityWarningEnabled = useFeatureFlag('issues_react_low_quality_comment_warning')

  const [state, setState] = useState<LowQualityWarningState>(LowQualityWarningState.Idle)
  const shownRef = useRef(false)

  const lowQualityCheckCallback = useCallback(
    (text: string) => {
      if (
        lowQualityWarningEnabled &&
        text.trim().length >= MIN_LENGTH_FOR_LOW_QUALITY_CHECK &&
        isLowQualityComment(text)
      ) {
        startTransition(() => setState(LowQualityWarningState.Showing))
      }
    },
    [lowQualityWarningEnabled],
  )

  const checkLowQualityComment = useDebounce(lowQualityCheckCallback, 500, {leading: false, trailing: true})

  useEffect(() => {
    if (state === LowQualityWarningState.Idle && markdownBody.trim().length > 0) {
      checkLowQualityComment(markdownBody)
    }
  }, [markdownBody, state, checkLowQualityComment])

  useEffect(() => {
    if (state === LowQualityWarningState.Showing && !shownRef.current) {
      shownRef.current = true
      sendAnalyticsEvent('comments.low_quality_warning.shown', 'LOW_QUALITY_COMMENT_WARNING')
    }
  }, [state, sendAnalyticsEvent])

  const handleDismiss = useCallback(() => {
    sendAnalyticsEvent('comments.low_quality_warning.dismissed', 'LOW_QUALITY_COMMENT_WARNING')
    setState(LowQualityWarningState.Dismissed)
    onDismiss?.()
  }, [sendAnalyticsEvent, onDismiss])

  const isShowing = state === LowQualityWarningState.Showing

  const banner = isShowing ? <LowQualityWarningBanner onDismiss={handleDismiss} /> : undefined

  return {banner, isShowing}
}
